/* eslint-disable max-lines */
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  completeCartDetails: {},
  senderDetails: {},
  suggestedCity: [],
  updateSenderData: {},
  frequentlyBoughtAddons: [],
  occasions: [],
  messageTemplates: {},
  occasionImgPath: "",
  messageCardDetails: {},
  cartExpiry: {},
  cartItemExpired: null,
  cartItemExpiredSlotLabel: "",
  cartSummaryAmountDetail: {},
  couponList: {},
  salutations: [],
  specialCountries: "",
  donationStatus: "",
  donationInfo: "",
  donationAmount: 0,
  carryBagInfo: "",
  carryBagAmount: 0,
  errorMsg: "",
  isCouponApplied: false,
  isProceedToPayError: false,
  isSetCartItemValuesError: false,
  isAddressLoading: false,
  completeCartLoader: true,
  cartSummaryLoader: false,
  giftItem: 0,
  cartItemIndex: 0,
  isCartItemIndex: false,
  checkoutActiveStep: "login",
  checkoutServiceabilityRescheduleRequest: null,
  checkoutLandingServiceability: {
    outOfStockByCartItemIndex: {},
    serviceItemsByPinCode: {},
  },
  productItemQuantityLimit: 9,
  showCombinedDeliveryAnimation: false,
  combinedDeliveryShippingPrice: 0,
  senderMobileOtp: {
    isLoading: false,
    isOtpSent: false,
    otpSentCount: 0,
    errorMessage: "",
    verifiedMobileNumber: "",
    verifiedCountryCode: "",
  },
};

const DeliveryAddressSlice = createSlice({
  name: "deliveryAddress",
  initialState,
  reducers: {
    requestToInitiateCheckoutPageDA: () => {},
    requestToFetchCouponList: () => {},
    requestToFetchCartAmountSummary: () => {},
    requestToSyncAutoCombinedCartSupplier: () => {},
    requestToDeleteCartItem: (state, action) => {
      {
        return { ...state, giftItem: action.payload.giftItemIndex, isCartItemIndex: false };
      }
    },
    setItemAttributeForOrderItem: () => {},
    requestToSetCartItemDetailsOnDA: (state, action) => {
      {
        return { ...state, giftItem: action.payload.itemIndex, isCartItemIndex: false };
      }
    },
    requestToFetchOccasionAndTemplates: () => {},
    requestToFetchMessageDetails: (state, action) => {
      {
        return { ...state, cartItemIndex: action.payload, isCartItemIndex: true };
      }
    },
    requestToSetCardDetails: () => {},
    requestToPostAddressForItem: (state, action) => {
      return {
        ...state,
        cartItemIndex: Number(Object.fromEntries(new URLSearchParams(action.payload))?.cartItemIndex),
        isCartItemIndex: true,
      };
    },
    requestToUpdateCartDataFromDA: (state) => {
      return {
        ...state,
        isProceedToPayError: false,
      };
    },
    requestToDeleteAddressForItem: () => {},
    requestToSetDonationStatus: () => {},
    responseToAddDonationAmount: () => {},
    responseToRemoveDonationAmount: () => {},
    requestToFetchDonationInfo: () => {},
    requestToFetchCarryBagInfo: () => {},
    requestToFetchCarryBagAmount: () => {},
    responseToAddCarryBag: () => {},
    responseToRemoveCarryBag: () => {},
    initiateCompleteCartLoader: (state) => {
      return {
        ...state,
        completeCartLoader: true,
      };
    },
    setCompleteCartDetails: (state, action) => {
      return { ...state, completeCartDetails: action.payload, completeCartLoader: false };
    },
    setGiftItemSpecialInstructions: (state, action) => {
      const { cartItemIndex, specialInstructions = "" } = action.payload || {};
      const orderList = state.completeCartDetails?.orderList;

      if (!orderList?.length || cartItemIndex === undefined || cartItemIndex === null) {
        return;
      }

      const orderIndex = orderList.findIndex(
        (order) => String(order?.giftItem?.cartItemIndex) === String(cartItemIndex),
      );

      if (orderIndex < 0) {
        return;
      }

      orderList[orderIndex].giftItem.specialInstructions = specialInstructions;
    },
    setSenderDetails: (state, action) => {
      return { ...state, senderDetails: action.payload };
    },
    requestToFetchCitySuggestionDetails: () => {},
    setCitySuggestion: (state, action) => {
      return { ...state, suggestedCity: action.payload };
    },
    setUpdateSenderData: (state, action) => {
      return { ...state, updateSenderData: action.payload };
    },
    setAddressLoading: (state, action) => {
      return { ...state, isAddressLoading: action.payload };
    },
    upsertFrequentlyAddon: (state, action) => {
      const addonData = action.payload;
      const cartItemIndex = addonData?.checkoutCartItemIndex;
      const productId = addonData?.parentItemDetails?.productId;
      const existingIndex = state.frequentlyBoughtAddons.findIndex((addon) => {
        if (cartItemIndex !== undefined && cartItemIndex !== null) {
          return String(addon?.checkoutCartItemIndex) === String(cartItemIndex);
        }

        return productId && String(addon?.parentItemDetails?.productId) === String(productId);
      });

      if (existingIndex >= 0) {
        state.frequentlyBoughtAddons[existingIndex] = addonData;
      } else {
        state.frequentlyBoughtAddons.push(addonData);
      }
    },

    setListOfOccasionAndMsgTemplate: (state, action) => {
      state.occasions = action.payload?.occasionlist || [];
      state.messageTemplates = action.payload?.messagetemplatelist || {};
      state.occasionImgPath = action.payload?.imgPath || "";
    },

    setMessageCardDetails: (state, action) => {
      state.messageCardDetails = action.payload;
    },
    setCartItemExpiryDetails: (state, action) => {
      return { ...state, cartExpiry: action.payload };
    },
    setCartItemExpiryIndex: (state, action) => {
      return {
        ...state,
        cartItemExpired: action.payload,
        cartItemExpiredSlotLabel: action.payload === null ? "" : state.cartItemExpiredSlotLabel,
      };
    },
    setCartItemExpiredDetails: (state, action) => {
      if (action.payload === null) {
        state.cartItemExpired = null;
        state.cartItemExpiredSlotLabel = "";
        return;
      }

      state.cartItemExpired = action.payload?.cartItemIndex ?? null;
      state.cartItemExpiredSlotLabel = action.payload?.expiredSlotLabel || "";
    },
    requestToSetCoupon: () => {},
    requestToRemoveCoupon: () => {},
    setErrorMsg: (state, action) => {
      return { ...state, errorMsg: action.payload };
    },

    responseFromSetCoupon: (state, action) => {
      return {
        ...state,
        cartSummaryAmountDetail: {
          loadingCoupon: false,
          cartSummaryAmountDetail: {
            ...state.cartSummaryAmountDetail,
            priceDetails: {
              ...state.cartSummaryAmountDetail.paymentInfo,
              discount: action.payload.totalDiscountAmount,
              totalShipping: action.payload.orderShippingTotal,
              additionalPromoAdjustment: action.payload.additionalPromoAdjustment,
              total: action.payload.orderGrandTotal,
              couponCode: action.payload.couponCode,
              promoMsg: action.payload.promoMsg,
              termAndConditions: action.payload.termAndConditions,
              labelText: action.payload.labelText,
              totalServiceCharge: action.payload.totalServiceCharge,
            },
          },
          isBinPromoCodeApplied: action.payload?.isBinPromoCodeApplied,
        },
      };
    },
    responseToRemoveCoupon: (state, action) => {
      return {
        ...state,
        cartSummaryAmountDetail: {
          loadingCoupon: false,
          couponError: null,
          showCouponInput: false,
          cartSummaryAmountDetail: {
            ...state.cartSummaryAmountDetail,
            priceDetails: {
              ...state.cartSummaryAmountDetail.paymentInfo,
              discount: action.payload.totalDiscountAmount,
              totalShipping: action.payload.orderShippingTotal,
              additionalPromoAdjustment: action.payload.additionalPromoAdjustment,
              total: action.payload.orderGrandTotal,
              couponCode: action.payload.couponCode,
              promoMsg: action.payload.promoMsg,
              termAndConditions: action.payload.termAndConditions,
              labelText: action.payload.labelText,
              totalServiceCharge: action.payload.totalServiceCharge,
            },
          },
          isBinPromoCodeApplied: action.payload?.isBinPromoCodeApplied,
        },
      };
    },

    setCartAmountSummary: (state, action) => {
      return { ...state, cartSummaryAmountDetail: action.payload };
    },
    setCartSummaryLoader: (state, action) => {
      return { ...state, cartSummaryLoader: action.payload };
    },

    setCouponList: (state, action) => {
      return { ...state, couponList: action.payload };
    },
    requestToFetchSpecialCountries: () => {},
    setSpecialCountry: (state, action) => {
      state.specialCountries = action.payload;
    },

    setDonateToCry: (state, action) => {
      state.cartAmountSummary = { ...state.cartAmountSummary };
      state.cartAmountSummary.donate = action.payload;
    },
    setDonationStatus: (state, action) => {
      return { ...state, donationStatus: action.payload };
    },
    setDonationAmount: (state, action) => {
      return { ...state, donationAmount: action.payload };
    },

    setDonationInfo: (state, action) => {
      return { ...state, donationInfo: action.payload };
    },
    setCarryBagInfo: (state, action) => {
      return { ...state, carryBagInfo: action.payload };
    },
    setCarryBagAmount: (state, action) => {
      return { ...state, carryBagAmount: action.payload };
    },
    setIsCouponApplied: (state, action) => {
      return { ...state, isCouponApplied: action.payload };
    },
    setIsProceedToPayError: (state, action) => {
      return { ...state, isProceedToPayError: action.payload };
    },
    setIsSetCartItemValuesError: (state, action) => {
      return { ...state, isSetCartItemValuesError: Boolean(action.payload) };
    },
    setCheckoutActiveStep: (state, action) => {
      state.checkoutActiveStep = action.payload;
    },
    requestCheckoutServiceabilityReschedule: (state, action) => {
      state.checkoutServiceabilityRescheduleRequest = {
        ...action.payload,
        requestId: Date.now(),
      };
    },
    clearCheckoutServiceabilityRescheduleRequest: (state) => {
      state.checkoutServiceabilityRescheduleRequest = null;
    },
    /** Applies delivery-date nudges for many cart items, then one cart refresh (no re-serviceability). */
    requestToApplyCheckoutDeliveryDateUpdates: () => {},
    setCheckoutLandingServiceability: (state, action) => {
      state.checkoutLandingServiceability = action.payload;
    },
    mergeCheckoutServiceabilityForPinCode: (state, action) => {
      const { pinCode, serviceItems, outOfStockByCartItemIndex } = action.payload;

      state.checkoutLandingServiceability.serviceItemsByPinCode[pinCode] = serviceItems;
      state.checkoutLandingServiceability.outOfStockByCartItemIndex = {
        ...state.checkoutLandingServiceability.outOfStockByCartItemIndex,
        ...outOfStockByCartItemIndex,
      };
    },
    requestToResetDelivery: () => {},
    setProductItemQuantityLimit: (state, action) => {
      return { ...state, productItemQuantityLimit: action.payload };
    },
    setShowCombinedDeliveryAnimation: (state, action) => {
      return { ...state, showCombinedDeliveryAnimation: action.payload };
    },
    setCombinedDeliveryShippingPrice: (state, action) => {
      return { ...state, combinedDeliveryShippingPrice: action.payload || 0 };
    },
    requestToSendSenderMobileOtp: (state) => {
      return { ...state, senderMobileOtp: { ...state.senderMobileOtp, isLoading: true, errorMessage: "" } };
    },
    setSenderMobileOtpSent: (state) => {
      return {
        ...state,
        senderMobileOtp: {
          ...state.senderMobileOtp,
          isLoading: false,
          isOtpSent: true,
          otpSentCount: state.senderMobileOtp.otpSentCount + 1,
          errorMessage: "",
        },
      };
    },
    requestToVerifySenderMobileOtp: (state) => {
      return { ...state, senderMobileOtp: { ...state.senderMobileOtp, isLoading: true, errorMessage: "" } };
    },
    setSenderMobileOtpVerified: (state, action) => {
      return {
        ...state,
        senderMobileOtp: {
          isLoading: false,
          isOtpSent: false,
          otpSentCount: 0,
          errorMessage: "",
          verifiedMobileNumber: action.payload?.mobileNumber || "",
          verifiedCountryCode: `${action.payload?.countryCode || ""}`,
        },
      };
    },
    setSenderMobileOtpError: (state, action) => {
      return {
        ...state,
        senderMobileOtp: { ...state.senderMobileOtp, isLoading: false, errorMessage: action.payload || "" },
      };
    },
    resetSenderMobileOtp: (state) => {
      return { ...state, senderMobileOtp: { ...initialState.senderMobileOtp } };
    },
  },
});

export const {
  requestToInitiateCheckoutPageDA,
  setItemAttributeForOrderItem,
  setSenderDetails,
  requestToFetchCitySuggestionDetails,
  setCitySuggestion,
  setUpdateSenderData,
  requestToFetchOccasionAndTemplates,
  setListOfOccasionAndMsgTemplate,
  requestToFetchMessageDetails,
  setMessageCardDetails,
  requestToSetCardDetails,
  setCartAmountSummary,
  upsertFrequentlyAddon,
  requestToSetCartItemDetailsOnDA,
  setCompleteCartDetails,
  setGiftItemSpecialInstructions,
  requestToDeleteCartItem,
  setCartItemExpiryDetails,
  setCartItemExpiryIndex,
  setCartItemExpiredDetails,
  requestToSetCoupon,
  responseFromSetCoupon,
  requestToRemoveCoupon,
  responseToRemoveCoupon,
  requestToFetchSpecialCountries,
  setSpecialCountry,
  requestToPostAddressForItem,
  requestToDeleteAddressForItem,
  requestToFetchCartAmountSummary,
  requestToSyncAutoCombinedCartSupplier,
  requestToSetDonationStatus,
  setDonationStatus,
  setDonationAmount,
  responseToAddDonationAmount,
  responseToRemoveDonationAmount,
  requestToFetchDonationInfo,
  setDonationInfo,
  requestToFetchCarryBagInfo,
  setCarryBagInfo,
  requestToFetchCarryBagAmount,
  setCarryBagAmount,
  responseToAddCarryBag,
  responseToRemoveCarryBag,
  requestToFetchCouponList,
  setCouponList,
  setProductItemQuantityLimit,
  requestToUpdateCartDataFromDA,
  setErrorMsg,
  setIsCouponApplied,
  setIsProceedToPayError,
  setIsSetCartItemValuesError,
  setAddressLoading,
  initiateCompleteCartLoader,
  setCartSummaryLoader,
  setCheckoutActiveStep,
  requestCheckoutServiceabilityReschedule,
  clearCheckoutServiceabilityRescheduleRequest,
  requestToApplyCheckoutDeliveryDateUpdates,
  setCheckoutLandingServiceability,
  mergeCheckoutServiceabilityForPinCode,
  requestToResetDelivery,
  setShowCombinedDeliveryAnimation,
  setCombinedDeliveryShippingPrice,
  requestToSendSenderMobileOtp,
  setSenderMobileOtpSent,
  requestToVerifySenderMobileOtp,
  setSenderMobileOtpVerified,
  setSenderMobileOtpError,
  resetSenderMobileOtp,
} = DeliveryAddressSlice.actions;

export default DeliveryAddressSlice.reducer;
